import React, { useState, useRef } from 'react';
import S3MultipartUploader from '../services/S3MultipartUploader';
import './FileUploader.css';

function FileUploader({ ticketNumber }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);
  const uploadersRef = useRef({});

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const newFiles = selectedFiles.map(file => ({
      file,
      id: `${file.name}-${Date.now()}`,
      status: 'pending',
      progress: 0,
      error: null
    }));
    setFiles(prev => [...prev, ...newFiles]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);
    const newFiles = droppedFiles.map(file => ({
      file,
      id: `${file.name}-${Date.now()}`,
      status: 'pending',
      progress: 0,
      error: null
    }));
    setFiles(prev => [...prev, ...newFiles]);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const uploadFile = async (fileItem) => {
    const uploader = new S3MultipartUploader(
      fileItem.file,
      ticketNumber,
      (progress) => {
        setUploadProgress(prev => ({
          ...prev,
          [fileItem.id]: progress.overallPercent
        }));
      }
    );

    uploadersRef.current[fileItem.id] = uploader;

    try {
      setFiles(prev => prev.map(f => 
        f.id === fileItem.id ? { ...f, status: 'uploading', progress: 0 } : f
      ));

      await uploader.upload();

      setFiles(prev => prev.map(f =>
        f.id === fileItem.id ? { ...f, status: 'completed', progress: 100 } : f
      ));
    } catch (error) {
      const isCancelled = error.message === 'Upload aborted';
      setFiles(prev => prev.map(f =>
        f.id === fileItem.id
          ? { ...f, status: isCancelled ? 'cancelled' : 'error', error: isCancelled ? null : error.message }
          : f
      ));
    } finally {
      delete uploadersRef.current[fileItem.id];
    }
  };

  const handleUploadAll = async () => {
    setUploading(true);
    const pendingFiles = files.filter(f => f.status === 'pending' || f.status === 'error');
    
    for (const fileItem of pendingFiles) {
      await uploadFile(fileItem);
    }
    
    setUploading(false);
  };

  const handleRetryFile = async (fileItem) => {
    await uploadFile(fileItem);
  };

  const handleRemoveFile = (fileId) => {
    if (uploadersRef.current[fileId]) {
      // File is currently uploading — abort it; the uploadFile catch will set status to 'cancelled'
      uploadersRef.current[fileId].abort();
    } else {
      setFiles(prev => prev.filter(f => f.id !== fileId));
    }
  };

  const handleClearCompleted = () => {
    setFiles(prev => prev.filter(f => f.status !== 'completed'));
  };

  return (
    <div className="file-uploader">
      <div
        className="drop-zone"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="drop-zone-content">
          <svg className="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p className="drop-zone-text">Drop files here or click to browse</p>
          <p className="drop-zone-subtext">Supports .mxf, .zip and other large files</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>

      {files.length > 0 && (
        <div className="files-section">
          <div className="files-header">
            <h3>Files ({files.length})</h3>
            <div className="files-actions">
              <button
                onClick={handleClearCompleted}
                className="btn btn-secondary"
                disabled={!files.some(f => f.status === 'completed')}
              >
                Clear Completed
              </button>
              <button
                onClick={handleUploadAll}
                className="btn btn-primary"
                disabled={uploading || !files.some(f => f.status === 'pending' || f.status === 'error')}
              >
                {uploading ? 'Uploading...' : 'Upload All'}
              </button>
            </div>
          </div>

          <div className="files-list">
            {files.map(fileItem => (
              <div key={fileItem.id} className={`file-item ${fileItem.status}`}>
                <div className="file-info">
                  <div className="file-name">{fileItem.file.name}</div>
                  <div className="file-size">{formatFileSize(fileItem.file.size)}</div>
                </div>

                <div className="file-status">
                  {fileItem.status === 'pending' && (
                    <span className="status-badge pending">Pending</span>
                  )}
                  {fileItem.status === 'uploading' && (
                    <div className="progress-container">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${uploadProgress[fileItem.id] || 0}%` }}
                        />
                      </div>
                      <span className="progress-text">
                        {Math.round(uploadProgress[fileItem.id] || 0)}%
                      </span>
                    </div>
                  )}
                  {fileItem.status === 'completed' && (
                    <span className="status-badge completed">✓ Completed</span>
                  )}
                  {fileItem.status === 'error' && (
                    <span className="status-badge error">Error: {fileItem.error}</span>
                  )}
                  {fileItem.status === 'cancelled' && (
                    <span className="status-badge cancelled">Cancelled</span>
                  )}
                </div>

                {fileItem.status === 'pending' && (
                  <button
                    onClick={() => handleRetryFile(fileItem)}
                    className="btn-retry"
                    disabled={uploading}
                    title="Upload this file"
                  >
                    &#8679;
                  </button>
                )}
                {(fileItem.status === 'cancelled' || fileItem.status === 'error') && (
                  <button
                    onClick={() => handleRetryFile(fileItem)}
                    className="btn-retry"
                    disabled={uploading}
                    title="Retry upload"
                  >
                    &#8635;
                  </button>
                )}

                <button
                  onClick={() => handleRemoveFile(fileItem.id)}
                  className="btn-remove"
                  title={fileItem.status === 'uploading' ? 'Cancel upload' : 'Remove'}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default FileUploader;
